import React, { useCallback, useMemo, useState } from 'react';

import { Price } from './Price';

import background from '../img/squareLines.png';

export const Pricing = React.memo(() => {
  const [selected, setSelected] = useState('monthly');
  const [scriptLoaded, setScriptLoaded] = useState(false);

  const handleCalendly = useCallback(
    (e) => {
      e.preventDefault();

      // Load the script dynamically only on the first click
      if (!scriptLoaded && !window.Calendly) {
        const link = document.createElement('link');
        link.href = 'https://assets.calendly.com/assets/external/widget.css';
        link.rel = 'stylesheet';
        document.head.appendChild(link);

        const script = document.createElement('script');
        script.src = 'https://assets.calendly.com/assets/external/widget.js';
        script.type = 'text/javascript';
        script.async = true;
        script.onload = () => {
          setScriptLoaded(true);
          if (window.Calendly) {
            window.Calendly.initPopupWidget({
              url: 'https://calendly.com/webcrumbs/chill-talk-webdev',
            });
          }
        };
        document.body.appendChild(script);
      } else if (window.Calendly) {
        // Script already loaded, initialize the widget
        window.Calendly.initPopupWidget({
          url: 'https://calendly.com/webcrumbs/chill-talk-webdev',
        });
      }
    },
    [scriptLoaded]
  );

  const handleMonthly = useCallback(() => {
    setSelected('monthly');
  }, []);
  const handleAnnual = useCallback(() => {
    setSelected('annual');
  }, []);

  const prices = useMemo(
    () => [
      {
        title: 'Basic',
        price: 'Free',
        heading: 'Start your journey with Frontend AI and code stunning interfaces',
        features: ['Limited AI Generations', 'Limited Image to Code'],
        cta: 'Start for free',
        onCta: () => {
          window.open('https://app.webcrumbs.ai/', '_blank');
        },
      },
      {
        recommended: true,
        title: 'Pro',
        dollar: '$',
        price: {
          monthly: '14',
          annual: '8',
        },
        heading: {
          monthly: (
            <>
              Per month, per seat,
              <br />
              billed monthly.
            </>
          ),
          annual: (
            <>
              Per month, per seat,
              <br />
              billed annually.
            </>
          ),
        },
        features: ['Unlimited AI Generations', 'Unlimited Image to Code', 'Priority access to new features'],
        cta: 'Be a PRO',
        onCta: {
          monthly: () => {
            window.open('https://app.webcrumbs.ai/frontend-ai?price=cN2aGH0dD6xR0cU8wG', '_blank');
          },
          annual: () => {
            window.open('https://app.webcrumbs.ai/frontend-ai?price=28obKLd0pcWf0cU5kv', '_blank');
          },
        },
      },
      {
        attention: true,
        title: 'Teams',
        price: "Let's talk!",
        heading: 'Customize your plan and get in touch with us',
        features: ['Collaborative workspace', 'Custom elements', 'Dedicated support'],
        cta: 'Contact us',
        onCta: handleCalendly,
      },
    ],
    [handleCalendly]
  );

  return (
    <div id="pricing" className="mb-[144px] flex flex-col bg-white">
      <div className="flex flex-col items-center justify-center p-[20px]" data-aos="fade-up" data-aos-duration="500">
        <h1 className="text-[17.02px] font-[700] leading-[30.6px] text-[#FA5B30] ">Pricing</h1>
        <h1 className="mt-[12px] text-center text-[45px] font-[700] leading-[67.2px] tracking-[-1.5px] lg:text-[54.03px] ">
          Grow with us
        </h1>
        <p className="mt-[20px] max-w-[660px] text-center text-[16.59px] font-[400] leading-[30.6px] text-neutral-800">
          Our prices will go up as we add more features.
          <br />
          Join us today to lock in your rate and enjoy all future updates at no extra cost.
        </p>
        <div className="mt-[22px] w-[210px] rounded-full bg-[#F8F4F1] p-2">
          <div className="relative flex items-center gap-2 rounded-full p-1">
            <div
              className={`absolute left-0 top-0 h-full w-1/2 rounded-full bg-[#FF9983] shadow-sm ${
                selected === 'annual' ? 'translate-x-full' : ''
              } transition-all duration-300 `}
            />
            <button
              className="z-10 flex h-[32px] w-1/2 items-center justify-center rounded-full text-sm font-medium text-[#120A0B] transition-all duration-300"
              onClick={handleMonthly}
            >
              Monthly
            </button>
            <button
              className="z-10 flex h-[32px] w-1/2 items-center justify-center rounded-full text-sm font-medium text-[#120A0B] transition-all duration-300"
              onClick={handleAnnual}
            >
              Annual
            </button>
          </div>
        </div>
        <div className="relative mt-[40px] grid grid-cols-1 gap-[12px] md:grid-cols-2 lg:grid-cols-3">
          {prices.map(({ recommended, attention, title, dollar, price, heading, features, cta, onCta }, index) => (
            <Price
              key={index}
              recommended={recommended}
              attention={attention}
              dollar={dollar}
              title={title}
              fullPrice={price?.monthly}
              price={price?.[selected] || price}
              heading={heading?.[selected] || heading}
              features={features}
              cta={cta}
              onCta={onCta?.[selected] || onCta}
            />
          ))}
        </div>
      </div>
      <img src={background} alt="background" className="-mt-[150px] size-full" />
    </div>
  );
});
